import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from "docx";

interface EstoppelData {
  buyerName: string;
  buyerAddress: string;
  sellerName: string;
  investorName: string;
  investorAddress: string;
  propertyAddress: string;
  originalContractDate: string;
  currentBalance: string;
  monthlyPayment: string;
  interestRate: string;
  paymentsRemaining: string;
  lastPaymentDate: string;
  lastPaymentAmount: string;
  hasBalloon: boolean;
  balloonAmount: string;
  balloonDueDate: string;
  propertyTaxesCurrent: string;
  insuranceCurrent: string;
  propertyCondition: string;
  additionalNotes: string;
}

const formatCurrency = (value: string): string => {
  if (!value) return "$0.00";
  const num = parseFloat(value);
  return isNaN(num) ? "$0.00" : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "Not specified";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const generateEstoppelWordDocument = async (data: EstoppelData): Promise<Blob> => {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          // Header
          new Paragraph({
            text: "ESTOPPEL CERTIFICATE",
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),

          new Paragraph({
            text: `Date: ${currentDate}`,
            alignment: AlignmentType.RIGHT,
            spacing: { after: 400 },
          }),

          // Introduction
          new Paragraph({
            children: [
              new TextRun({
                text: "TO: ",
                bold: true,
              }),
              new TextRun({
                text: data.buyerName,
              }),
            ],
            spacing: { after: 200 },
          }),

          ...(data.buyerAddress ? [
            new Paragraph({
              text: data.buyerAddress,
              spacing: { after: 200 },
            }),
          ] : []),

          new Paragraph({
            children: [
              new TextRun({
                text: "FROM: ",
                bold: true,
              }),
              new TextRun({
                text: data.investorName || "Contract Funding Group, LLC",
              }),
            ],
            spacing: { after: 200 },
          }),

          ...(data.investorAddress ? [
            new Paragraph({
              text: data.investorAddress,
              spacing: { after: 200 },
            }),
          ] : []),

          new Paragraph({
            children: [
              new TextRun({
                text: "RE: ",
                bold: true,
              }),
              new TextRun({
                text: `Land Contract for Property Located at ${data.propertyAddress}`,
              }),
            ],
            spacing: { after: 400 },
          }),

          // Body paragraphs
          new Paragraph({
            text: "The undersigned (\"Buyer\") certifies to you and any prospective assignee or purchaser of the Land Contract (the \"Contract\") that the following statements are true and correct as of the date hereof:",
            spacing: { after: 400 },
          }),

          // Section I - Contract Information
          new Paragraph({
            text: "I. CONTRACT INFORMATION",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "1. Property Address: ",
                bold: true,
              }),
              new TextRun({
                text: data.propertyAddress,
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "2. Current Principal Balance: ",
                bold: true,
              }),
              new TextRun({
                text: formatCurrency(data.currentBalance),
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "3. Monthly Payment Amount: ",
                bold: true,
              }),
              new TextRun({
                text: formatCurrency(data.monthlyPayment),
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "4. Interest Rate: ",
                bold: true,
              }),
              new TextRun({
                text: data.interestRate ? `${data.interestRate}% per annum` : "Not specified",
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "5. Number of Payments Remaining: ",
                bold: true,
              }),
              new TextRun({
                text: data.paymentsRemaining || "Not specified",
              }),
            ],
            spacing: { after: 200 },
          }),

          ...(data.hasBalloon ? [
            new Paragraph({
              children: [
                new TextRun({
                  text: "6. Balloon Payment: ",
                  bold: true,
                }),
                new TextRun({
                  text: `${formatCurrency(data.balloonAmount)} due on ${formatDate(data.balloonDueDate)}`,
                }),
              ],
              spacing: { after: 200 },
            }),
          ] : []),

          // Section II - Payment History
          new Paragraph({
            text: "II. PAYMENT HISTORY",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "1. Last Payment Date: ",
                bold: true,
              }),
              new TextRun({
                text: formatDate(data.lastPaymentDate),
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "2. Last Payment Amount: ",
                bold: true,
              }),
              new TextRun({
                text: formatCurrency(data.lastPaymentAmount),
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "3. Payment Status: ",
                bold: true,
              }),
              new TextRun({
                text: "All payments are current and up to date. There are no defaults or late payments outstanding.",
              }),
            ],
            spacing: { after: 200 },
          }),

          // Section III - Property Status
          new Paragraph({
            text: "III. PROPERTY STATUS",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "1. Property Taxes: ",
                bold: true,
              }),
              new TextRun({
                text: `Current status: ${data.propertyTaxesCurrent}`,
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "2. Property Insurance: ",
                bold: true,
              }),
              new TextRun({
                text: `Current status: ${data.insuranceCurrent}`,
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "3. Property Condition: ",
                bold: true,
              }),
              new TextRun({
                text: data.propertyCondition,
              }),
            ],
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "4. Occupancy: ",
                bold: true,
              }),
              new TextRun({
                text: "The undersigned currently occupies the property as a primary residence.",
              }),
            ],
            spacing: { after: 200 },
          }),

          // Section IV - Certifications
          new Paragraph({
            text: "IV. BUYER CERTIFICATIONS",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),

          new Paragraph({
            text: "The Buyer certifies the following:",
            spacing: { after: 200 },
          }),

          new Paragraph({
            text: "• All terms and conditions of the Contract remain in full force and effect.",
            spacing: { after: 100 },
          }),

          new Paragraph({
            text: "• There are no defaults, breaches, or violations of the Contract by any party.",
            spacing: { after: 100 },
          }),

          new Paragraph({
            text: "• No modifications or amendments have been made to the Contract except as may be noted herein.",
            spacing: { after: 100 },
          }),

          new Paragraph({
            text: "• Buyer has been notified of the potential assignment of the Contract and agrees to make all future payments to the new assignee as directed.",
            spacing: { after: 100 },
          }),

          new Paragraph({
            text: "• All information provided in this Estoppel Certificate is true, accurate, and complete to the best of Buyer's knowledge.",
            spacing: { after: 400 },
          }),

          // Additional Notes
          ...(data.additionalNotes ? [
            new Paragraph({
              text: "V. ADDITIONAL NOTES",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 400, after: 200 },
            }),
            new Paragraph({
              text: data.additionalNotes,
              spacing: { after: 400 },
            }),
          ] : []),

          // Signature Section
          new Paragraph({
            text: "IN WITNESS WHEREOF, the Buyer has executed this Estoppel Certificate as of the date first written above.",
            spacing: { before: 400, after: 400 },
          }),

          new Paragraph({
            text: "_".repeat(60),
            spacing: { before: 600, after: 100 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Signature of Buyer",
                bold: true,
              }),
            ],
            spacing: { after: 400 },
          }),

          new Paragraph({
            text: "_".repeat(60),
            spacing: { after: 100 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Printed Name: ",
                bold: true,
              }),
              new TextRun({
                text: data.buyerName,
              }),
            ],
            spacing: { after: 400 },
          }),

          new Paragraph({
            text: "_".repeat(60),
            spacing: { after: 100 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Date",
                bold: true,
              }),
            ],
            spacing: { after: 400 },
          }),

          new Paragraph({
            text: "_".repeat(60),
            spacing: { after: 100 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Address: ",
                bold: true,
              }),
              new TextRun({
                text: data.buyerAddress || "___________________________",
              }),
            ],
            spacing: { after: 400 },
          }),

          // Footer note
          new Paragraph({
            text: "This Estoppel Certificate is provided for informational purposes in connection with the proposed assignment or sale of the Land Contract. The Buyer acknowledges that this certification may be relied upon by prospective assignees or purchasers.",
            spacing: { before: 600 },
            alignment: AlignmentType.JUSTIFIED,
          }),
        ],
      },
    ],
  });

  return await Packer.toBlob(doc);
};
